/**
 * Format XRP amount for display
 */
export function formatXRP(amount: string | number): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (isNaN(num)) {
    return '0';
  }

  // Format with appropriate decimal places
  if (num >= 1000000) {
    return (num / 1000000).toFixed(2) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(2) + 'K';
  } else if (num >= 1) {
    return num.toFixed(2);
  } else if (num >= 0.001) {
    return num.toFixed(3);
  } else {
    return num.toFixed(6);
  }
}

/**
 * Parse XRP amount from string
 */
export function parseXRP(amount: string): number {
  const parsed = parseFloat(amount);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Validate XRP amount
 */
export function validateXRPAmount(amount: string): { valid: boolean; error?: string } {
  if (!amount || amount.trim() === '') {
    return { valid: false, error: 'Amount is required' };
  }

  const num = parseFloat(amount);

  if (isNaN(num)) {
    return { valid: false, error: 'Invalid amount format' };
  }

  if (num <= 0) {
    return { valid: false, error: 'Amount must be positive' };
  }

  if (num > 100000000) { // 100M XRP max
    return { valid: false, error: 'Amount too large' };
  }

  if (num < 0.000001) { // 1 drop minimum
    return { valid: false, error: 'Amount too small (minimum 0.000001 XRP)' };
  }

  return { valid: true };
}

/**
 * Convert XRP to drops (1 XRP = 1,000,000 drops)
 * Uses integer math to avoid floating-point precision issues
 */
export function xrpToDrops(xrp: string | number): string {
  const num = typeof xrp === 'string' ? parseFloat(xrp) : xrp;
  if (isNaN(num)) return '0';
  // Round to avoid floating-point issues, then convert to integer drops
  return Math.round(num * 1000000).toString();
}

/**
 * Convert drops to XRP
 * Returns a number to avoid floating-point string representation issues
 */
export function dropsToXrp(drops: string | number | null | undefined): number {
  if (drops === null || drops === undefined) return 0;
  const num = typeof drops === 'string' ? parseInt(drops, 10) : drops;
  if (isNaN(num)) return 0;
  // Use integer division then add remainder to avoid floating-point issues
  const wholeXrp = Math.floor(num / 1000000);
  const remainder = num % 1000000;
  return wholeXrp + remainder / 1000000;
}

/**
 * Convert drops to whole XRP (rounded to nearest integer)
 * Use this for mint prices that should be whole XRP only
 */
export function dropsToWholeXrp(drops: string | number | null | undefined): number {
  if (drops === null || drops === undefined) return 0;
  const num = typeof drops === 'string' ? parseInt(drops, 10) : drops;
  if (isNaN(num)) return 0;
  return Math.round(num / 1000000);
}

/**
 * Convert whole XRP to drops
 * Use this for mint prices that should be whole XRP only
 */
export function wholeXrpToDrops(xrp: number): string {
  if (isNaN(xrp)) return '0';
  return (Math.round(xrp) * 1000000).toString();
}

/**
 * Format currency amount with symbol
 */
export function formatCurrency(
  amount: string | number,
  currency: string = 'XRP',
  showSymbol: boolean = true
): string {
  const formatted = currency === 'XRP' ? formatXRP(amount) : amount.toString();
  return showSymbol ? `${formatted} ${currency}` : formatted;
}

/**
 * Calculate percentage change
 */
export function calculatePercentageChange(oldValue: number, newValue: number): number {
  if (oldValue === 0) return 0;
  return ((newValue - oldValue) / oldValue) * 100;
}

/**
 * Format percentage for display
 */
export function formatPercentage(percentage: number, decimals: number = 2): string {
  return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(decimals)}%`;
}